import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  type RadioGroupProps,
} from '@mui/material';
import { type ReactNode, useId } from 'react';

import { type ConfigFieldProps } from './types';
import { useSelectedNodeField } from './useSelectedNodeField';
import { useReactFlowViewContext } from '../../context/context';

interface RadioGroupFieldOption<TValue extends string> {
  label: ReactNode;
  value: TValue;
}

interface RadioGroupFieldProps<TValue extends string>
  extends ConfigFieldProps, Pick<RadioGroupProps, 'row' | 'defaultValue'> {
  label: Required<ReactNode>;
  helperText?: ReactNode;
  options: RadioGroupFieldOption<TValue>[];
  defaultValue?: TValue;
}

export function BlockRadioGroupField<TValue extends string>(props: RadioGroupFieldProps<TValue>) {
  const { fieldName, label, helperText, options, ...radioGroupProps } = props;
  const { field, isFieldErrorVisible, visibleError } = useSelectedNodeField<TValue>(fieldName);
  const { isEditable } = useReactFlowViewContext();
  const visibleHelperText = visibleError ?? helperText;

  const fieldId = useId();
  return (
    <FormControl error={isFieldErrorVisible} variant="standard">
      <FormLabel id={fieldId}>{label}</FormLabel>
      <RadioGroup
        aria-labelledby={fieldId}
        {...radioGroupProps}
        {...field}
        value={field.value ?? radioGroupProps.defaultValue ?? ''}
        aria-disabled={!isEditable}
      >
        {options.map((option) => {
          const { label, value } = option;
          return (
            <FormControlLabel
              key={value}
              value={value ?? label}
              disabled={!isEditable}
              control={<Radio />}
              label={label}
            />
          );
        })}
      </RadioGroup>
      {visibleHelperText && <FormHelperText>{visibleHelperText}</FormHelperText>}
    </FormControl>
  );
}
